import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import api from "../api/api";

import {
    useAcademicYear,
} from "../AcademicYearContext";

import {
    pdf,
} from "@react-pdf/renderer";

import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";

import {
    BarChartOutlined,
    BankOutlined,
    CheckCircleOutlined,
    CloudDownloadOutlined,
    DollarOutlined,
    FilePdfOutlined,
    PieChartOutlined,
    ReloadOutlined,
    RiseOutlined,
    TeamOutlined,
    WarningOutlined,
} from "@ant-design/icons";

import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import {
    ExecutiveFinancialReportPDF,
    GradeFinancialReportPDF,
    StudentTypeFinancialReportPDF,
} from "./SchoolFinancialReportPDF";

const {
    Title,
    Text,
} = Typography;

const API_ENDPOINT =
    "/school-financial-report/";

const INSTALLMENT_ORDER = [
    "Registration",
    "1st Installment",
    "2nd Installment",
    "3rd Installment",
    "4th Installment",
];

const STUDENT_TYPE_ORDER = [
    "old",
    "new",
    "scholarship",
    "ward",
];

const STUDENT_TYPE_LABELS = {
    old: "Old Students",
    new: "New Students",
    scholarship: "Scholarship Students",
    ward: "Ward Students",
};

const INSTALLMENT_LABELS = {
    Registration: "Registration",
    "1st Installment": "1st Installment",
    "2nd Installment": "2nd Installment",
    "3rd Installment": "3rd Installment",
    "4th Installment": "4th Installment",
};

const moneyFormatter =
    new Intl.NumberFormat(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    );

const numberFormatter =
    new Intl.NumberFormat(
        "en-US"
    );

const formatMoney = (
    value
) => {

    const number =
        Number(
            value || 0
        );

    return moneyFormatter.format(
        Number.isFinite(number)
            ? number
            : 0
    );
};

const formatNumber = (
    value
) => {

    const number =
        Number(
            value || 0
        );

    return numberFormatter.format(
        Number.isFinite(number)
            ? number
            : 0
    );
};

const percentage = (
    collected,
    total
) => {

    const totalValue =
        Number(
            total || 0
        );

    const collectedValue =
        Number(
            collected || 0
        );

    if (
        totalValue <= 0
    ) {
        return 0;
    }

    return Math.min(
        100,
        Math.max(
            0,
            (
                collectedValue
                /
                totalValue
            )
            *
            100
        )
    );
};

const getCollectionColor = (
    collected,
    total
) => {

    const rate =
        percentage(
            collected,
            total
        );

    if (rate >= 90) {
        return "success";
    }

    if (rate >= 70) {
        return "processing";
    }

    if (rate >= 40) {
        return "warning";
    }

    return "error";
};

const normalizeReport = (
    response
) => {

    const data =
        response?.data || {};

    return {

        academic_year:
            data.academic_year || {},

        summary:
            data.summary || {
                total_students: 0,
                total_fee: "0",
                total_collected: "0",
                outstanding_balance: "0",
            },

        installment_breakdown:
            data.installment_breakdown || {},

        student_type_breakdown:
            data.student_type_breakdown || {},

        grade_class_breakdown:
            Array.isArray(
                data.grade_class_breakdown
            )
                ? data.grade_class_breakdown
                : [],

    };
};

const downloadBlob = (
    blob,
    filename
) => {

    const url =
        window.URL.createObjectURL(
            blob
        );

    const anchor =
        document.createElement(
            "a"
        );

    anchor.href =
        url;

    anchor.download =
        filename;

    document.body.appendChild(
        anchor
    );

    anchor.click();

    anchor.remove();

    window.setTimeout(
        () => {
            window.URL.revokeObjectURL(
                url
            );
        },
        1000
    );
};


/* ==========================================================
   VISUAL THEME ONLY
   NO BUSINESS LOGIC
========================================================== */

const financialReportStyles = `
/* ==========================================================
   GLOBAL REPORT
========================================================== */

.school-financial-report {
    --fr-navy: #111827;
    --fr-blue: #2563eb;
    --fr-indigo: #4f46e5;
    --fr-purple: #7c3aed;
    --fr-violet: #8b5cf6;
    --fr-pink: #db2777;
    --fr-orange: #f97316;
    --fr-gold: #eab308;
    --fr-green: #16a34a;
    --fr-emerald: #059669;
    --fr-cyan: #0891b2;
    --fr-red: #dc2626;
    --fr-slate: #334155;

    width: 100%;
    min-height: 100vh;
    overflow-x: hidden;

    animation:
        financialPageEnter
        .45s
        cubic-bezier(.22, 1, .36, 1)
        both;
}

.school-financial-report *,
.school-financial-report *::before,
.school-financial-report *::after {
    box-sizing: border-box;
}

@keyframes financialPageEnter {

    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}


/* ==========================================================
   GENERAL CARD ANIMATION
========================================================== */

.school-financial-report .ant-card {
    position: relative;
    overflow: hidden;

    border: 1px solid rgba(148, 163, 184, .15);

    transition:
        transform .28s cubic-bezier(.22, 1, .36, 1),
        box-shadow .28s ease,
        border-color .28s ease;
}

.school-financial-report .ant-card:hover {
    transform: translateY(-3px);

    box-shadow:
        0 18px 45px
        rgba(15, 23, 42, .12);
}

.school-financial-report .ant-card-head {
    border-bottom-color:
        rgba(148, 163, 184, .14);
}

.school-financial-report .ant-card-head-title {
    font-weight: 800;
}


/* ==========================================================
   HEADER
========================================================== */

.school-financial-report .financial-main-header {
    position: relative;
    overflow: hidden;

    background:
        linear-gradient(
            135deg,
            #020617 0%,
            #111827 30%,
            #1e1b4b 62%,
            #581c87 100%
        ) !important;

    border: none !important;

    box-shadow:
        0 18px 45px
        rgba(15, 23, 42, .22) !important;
}

.school-financial-report .financial-main-header::before {
    content: "";

    position: absolute;

    width: 330px;
    height: 330px;

    right: -120px;
    top: -170px;

    border-radius: 50%;

    background:
        radial-gradient(
            circle,
            rgba(236, 72, 153, .30),
            transparent 68%
        );

    pointer-events: none;
}

.school-financial-report .financial-main-header::after {
    content: "";

    position: absolute;

    width: 260px;
    height: 260px;

    left: -120px;
    bottom: -160px;

    border-radius: 50%;

    background:
        radial-gradient(
            circle,
            rgba(34, 211, 238, .20),
            transparent 68%
        );

    pointer-events: none;
}


/* ==========================================================
   KPI CARDS
========================================================== */

.school-financial-report .financial-kpi-card {
    position: relative;
    overflow: hidden;

    border: none !important;

    color: #fff;

    transition:
        transform .30s cubic-bezier(.22, 1, .36, 1),
        box-shadow .30s ease;
}

.school-financial-report .financial-kpi-card:hover {
    transform:
        translateY(-7px)
        scale(1.012);
}

.school-financial-report .financial-kpi-card::before {
    content: "";

    position: absolute;

    width: 190px;
    height: 190px;

    right: -80px;
    top: -90px;

    border-radius: 50%;

    background:
        rgba(255, 255, 255, .16);

    transition:
        transform .5s
        cubic-bezier(.22, 1, .36, 1);
}

.school-financial-report .financial-kpi-card::after {
    content: "";

    position: absolute;

    width: 110px;
    height: 110px;

    left: -45px;
    bottom: -55px;

    border-radius: 50%;

    background:
        rgba(255, 255, 255, .08);
}

.school-financial-report .financial-kpi-card:hover::before {
    transform:
        scale(1.4)
        rotate(15deg);
}

.school-financial-report .financial-kpi-card .ant-statistic-title,
.school-financial-report .financial-kpi-card .ant-statistic-content,
.school-financial-report .financial-kpi-card .ant-statistic-content-prefix,
.school-financial-report .financial-kpi-card .ant-typography {
    position: relative;
    z-index: 2;
}

.school-financial-report .financial-kpi-card .ant-statistic-title {
    color:
        rgba(255,255,255,.78);

    font-weight: 700;
}

.school-financial-report .financial-kpi-card .ant-statistic-content {
    color: #fff;
}

.school-financial-report .financial-kpi-card .ant-statistic-content-prefix {
    color: #fff;
}

.school-financial-report .kpi-students {
    background:
        linear-gradient(
            135deg,
            #2563eb 0%,
            #4f46e5 48%,
            #7c3aed 100%
        ) !important;

    box-shadow:
        0 14px 35px
        rgba(37, 99, 235, .25);
}

.school-financial-report .kpi-fees {
    background:
        linear-gradient(
            135deg,
            #0891b2 0%,
            #0d9488 48%,
            #16a34a 100%
        ) !important;

    box-shadow:
        0 14px 35px
        rgba(8, 145, 178, .24);
}

.school-financial-report .kpi-collected {
    background:
        linear-gradient(
            135deg,
            #16a34a 0%,
            #059669 48%,
            #0d9488 100%
        ) !important;

    box-shadow:
        0 14px 35px
        rgba(22, 163, 74, .24);
}

.school-financial-report .kpi-outstanding {
    background:
        linear-gradient(
            135deg,
            #dc2626 0%,
            #ea580c 48%,
            #f97316 100%
        ) !important;

    box-shadow:
        0 14px 35px
        rgba(220, 38, 38, .24);
}


/* ==========================================================
   GENERAL SECTION COLORS
========================================================== */

.school-financial-report .report-section-card {
    background: #fff;
}

.school-financial-report .section-health {
    border-top: 4px solid #16a34a;
}

.school-financial-report .section-position {
    border-top: 4px solid #2563eb;
}

.school-financial-report .section-filters {
    border-top: 4px solid #7c3aed;
}

.school-financial-report .section-installments {
    border-top: 4px solid #f97316;
}

.school-financial-report .section-progression {
    border-top: 4px solid #0891b2;
}

.school-financial-report .section-grade-chart {
    border-top: 4px solid #4f46e5;
}

.school-financial-report .section-grade-table {
    border-top: 4px solid #111827;
}

.school-financial-report .section-student-table {
    border-top: 4px solid #eab308;
}


/* ==========================================================
   COLLECTION HEALTH
========================================================== */

.school-financial-report .collection-health-card {
    background:
        linear-gradient(
            135deg,
            #ecfdf5 0%,
            #f0fdf4 48%,
            #eff6ff 100%
        );
}


/* ==========================================================
   FINANCIAL POSITION
========================================================== */

.school-financial-report .financial-position-card {
    background:
        linear-gradient(
            135deg,
            #eff6ff 0%,
            #eef2ff 48%,
            #faf5ff 100%
        );
}


/* ==========================================================
   FILTER CARD
========================================================== */

.school-financial-report .financial-filter-card {
    background:
        linear-gradient(
            135deg,
            #ffffff 0%,
            #faf5ff 50%,
            #eff6ff 100%
        );

    border:
        1px solid
        rgba(124, 58, 237, .14);
}

.school-financial-report .financial-filter-card .ant-select-selector {
    transition:
        border-color .25s ease,
        box-shadow .25s ease;
}

.school-financial-report .financial-filter-card
.ant-select-selector:hover {
    border-color:
        #7c3aed !important;

    box-shadow:
        0 4px 16px
        rgba(124, 58, 237, .14);
}

.school-financial-report .financial-filter-card
.ant-select-focused
.ant-select-selector {
    border-color:
        #4f46e5 !important;

    box-shadow:
        0 0 0 3px
        rgba(79, 70, 229, .12) !important;
}


/* ==========================================================
   CHART CARDS
========================================================== */

.school-financial-report .chart-card {
    overflow: hidden;
}

.school-financial-report .chart-card::before {
    content: "";

    position: absolute;

    left: 0;
    top: 0;

    width: 100%;
    height: 4px;

    transform:
        scaleX(0);

    transform-origin:
        left center;

    transition:
        transform .35s
        cubic-bezier(.22, 1, .36, 1);
}

.school-financial-report .chart-card:hover::before {
    transform:
        scaleX(1);
}

.school-financial-report .installment-chart-card::before {
    background:
        linear-gradient(
            90deg,
            #f97316,
            #eab308,
            #16a34a
        );
}

.school-financial-report .student-chart-card::before {
    background:
        linear-gradient(
            90deg,
            #2563eb,
            #7c3aed,
            #db2777
        );
}

.school-financial-report .progression-chart-card::before {
    background:
        linear-gradient(
            90deg,
            #0891b2,
            #2563eb,
            #7c3aed
        );
}

.school-financial-report .grade-chart-card::before {
    background:
        linear-gradient(
            90deg,
            #4f46e5,
            #7c3aed,
            #db2777
        );
}


/* ==========================================================
   MAIN GRADE TABLE
========================================================== */

.school-financial-report .grade-financial-table {
    width: 100%;
}

.school-financial-report .grade-financial-table .ant-table {
    border-radius: 12px;
    overflow: hidden;
}

.school-financial-report .grade-financial-table
.ant-table-thead > tr > th {
    background:
        linear-gradient(
            135deg,
            #020617,
            #111827,
            #1e293b
        ) !important;

    color: #fff !important;

    font-weight: 800;

    border-bottom:
        none !important;

    white-space: nowrap;
}

.school-financial-report .grade-financial-table
.ant-table-tbody > tr {
    transition:
        background-color .30s ease,
        box-shadow .30s ease;
}

.school-financial-report .grade-financial-table
.ant-table-tbody > tr > td {
    transition:
        background-color .30s ease,
        color .30s ease,
        border-color .30s ease;
}

.school-financial-report .grade-financial-table
.ant-table-tbody > tr:hover {
    position: relative;
    z-index: 3;

    box-shadow:
        0 6px 20px
        rgba(0,0,0,.20);
}

.school-financial-report .grade-financial-table
.ant-table-tbody > tr:hover > td {
    background:
        #000 !important;

    color:
        #fff !important;
}

.school-financial-report .grade-financial-table
.ant-table-tbody > tr:hover
.ant-typography {
    color:
        #fff !important;
}

.school-financial-report .grade-financial-table
.ant-table-tbody > tr:hover
.anticon {
    color:
        #fff !important;
}

.school-financial-report .grade-financial-table
.ant-table-tbody > tr:hover
.ant-tag {
    color:
        #fff !important;
}


/* ==========================================================
   EXPAND ICON
========================================================== */

.school-financial-report .grade-financial-table
.ant-table-row-expand-icon {
    border-radius: 7px;

    transition:
        transform .25s
        cubic-bezier(.22, 1, .36, 1),
        background-color .25s ease,
        color .25s ease,
        border-color .25s ease;
}

.school-financial-report .grade-financial-table
.ant-table-row-expand-icon:hover {
    transform:
        scale(1.12);

    background:
        #7c3aed;

    border-color:
        #7c3aed;

    color:
        #fff;
}


/* ==========================================================
   EXPANDED GRADE SECTION
========================================================== */

.school-financial-report .grade-expanded-panel {
    position: relative;

    margin: 0;

    padding:
        20px !important;

    background:
        linear-gradient(
            135deg,
            #020617 0%,
            #0f172a 25%,
            #1e1b4b 48%,
            #312e81 70%,
            #581c87 100%
        ) !important;

    border-radius:
        0 0 14px 14px;

    overflow: hidden;

    animation:
        expandedGradeReveal
        .40s
        cubic-bezier(.22, 1, .36, 1)
        both;
}

@keyframes expandedGradeReveal {

    from {
        opacity: 0;
        transform:
            translateY(-7px);
    }

    to {
        opacity: 1;
        transform:
            translateY(0);
    }
}

.school-financial-report .grade-expanded-panel::before {
    content: "";

    position: absolute;

    width: 340px;
    height: 340px;

    right: -140px;
    top: -180px;

    border-radius: 50%;

    background:
        radial-gradient(
            circle,
            rgba(236, 72, 153, .34),
            transparent 68%
        );

    pointer-events:
        none;

    animation:
        expandedGlowOne
        7s
        ease-in-out
        infinite;
}

.school-financial-report .grade-expanded-panel::after {
    content: "";

    position: absolute;

    width: 280px;
    height: 280px;

    left: -140px;
    bottom: -170px;

    border-radius: 50%;

    background:
        radial-gradient(
            circle,
            rgba(34, 211, 238, .24),
            transparent 68%
        );

    pointer-events:
        none;

    animation:
        expandedGlowTwo
        8s
        ease-in-out
        infinite;
}

@keyframes expandedGlowOne {

    0%,
    100% {
        transform:
            translate3d(0, 0, 0)
            scale(1);
    }

    50% {
        transform:
            translate3d(-20px, 14px, 0)
            scale(1.08);
    }
}

@keyframes expandedGlowTwo {

    0%,
    100% {
        transform:
            translate3d(0, 0, 0)
            scale(1);
    }

    50% {
        transform:
            translate3d(20px, -10px, 0)
            scale(1.08);
    }
}

.school-financial-report .grade-expanded-title {
    position: relative;
    z-index: 2;

    color: #fff;

    font-size: 17px;

    font-weight: 900;

    margin-bottom:
        14px;

    letter-spacing:
        .2px;
}


/* ==========================================================
   EXPANDED TABLE SCROLL
========================================================== */

.school-financial-report .expanded-table-container {
    position: relative;
    z-index: 4;

    width: 100%;

    overflow-x: auto;
    overflow-y: hidden;

    -webkit-overflow-scrolling:
        touch;

    scroll-behavior:
        smooth;

    scrollbar-width:
        thin;

    scrollbar-color:
        rgba(255,255,255,.42)
        transparent;

    border-radius:
        12px;

    padding-bottom:
        3px;
}

.school-financial-report
.expanded-table-container
+ .expanded-table-container {
    margin-top:
        18px;
}

.school-financial-report
.expanded-table-container::-webkit-scrollbar {
    height:
        7px;
}

.school-financial-report
.expanded-table-container::-webkit-scrollbar-track {
    background:
        rgba(255,255,255,.08);

    border-radius:
        20px;
}

.school-financial-report
.expanded-table-container::-webkit-scrollbar-thumb {
    background:
        rgba(255,255,255,.45);

    border-radius:
        20px;
}


/* ==========================================================
   EXPANDED TABLES
========================================================== */

.school-financial-report
.expanded-financial-table {
    min-width:
        680px;
}

.school-financial-report
.expanded-financial-table
.ant-table {
    overflow:
        hidden;

    border-radius:
        12px;
}

.school-financial-report
.expanded-financial-table
.ant-table-thead > tr > th {
    color:
        #fff !important;

    border:
        none !important;

    font-weight:
        800;

    white-space:
        nowrap;
}

.school-financial-report
.expanded-student-table
.ant-table-thead > tr > th {
    background:
        linear-gradient(
            135deg,
            #6d28d9,
            #7c3aed,
            #db2777
        ) !important;
}

.school-financial-report
.expanded-installment-table
.ant-table-thead > tr > th {
    background:
        linear-gradient(
            135deg,
            #ea580c,
            #f97316,
            #eab308
        ) !important;
}

.school-financial-report
.expanded-financial-table
.ant-table-tbody > tr > td {
    font-weight:
        650;

    transition:
        background-color .27s ease,
        color .27s ease;
}

.school-financial-report
.expanded-student-table
.ant-table-tbody > tr:nth-child(odd) > td {
    background:
        #faf5ff;
}

.school-financial-report
.expanded-student-table
.ant-table-tbody > tr:nth-child(even) > td {
    background:
        #fdf2f8;
}

.school-financial-report
.expanded-installment-table
.ant-table-tbody > tr:nth-child(odd) > td {
    background:
        #fff7ed;
}

.school-financial-report
.expanded-installment-table
.ant-table-tbody > tr:nth-child(even) > td {
    background:
        #fffbeb;
}

.school-financial-report
.expanded-financial-table
.ant-table-tbody > tr:hover > td {
    background:
        #000 !important;

    color:
        #fff !important;
}

.school-financial-report
.expanded-financial-table
.ant-table-tbody > tr:hover
.ant-typography {
    color:
        #fff !important;
}


/* ==========================================================
   STUDENT TYPE TABLE
========================================================== */

.school-financial-report
.student-type-financial-table {
    width:
        100%;
}

.school-financial-report
.student-type-financial-table
.ant-table {
    overflow:
        hidden;

    border-radius:
        12px;
}

.school-financial-report
.student-type-financial-table
.ant-table-thead > tr > th {
    background:
        linear-gradient(
            135deg,
            #9d174d,
            #be185d,
            #7c3aed
        ) !important;

    color:
        #fff !important;

    font-weight:
        800;

    white-space:
        nowrap;

    border-bottom:
        none !important;
}

.school-financial-report
.student-type-financial-table
.ant-table-tbody > tr {
    transition:
        background-color .28s ease,
        box-shadow .28s ease;
}

.school-financial-report
.student-type-financial-table
.ant-table-tbody > tr > td {
    transition:
        background-color .28s ease,
        color .28s ease;
}

.school-financial-report
.student-type-financial-table
.ant-table-tbody > tr:hover {
    position:
        relative;

    z-index:
        2;

    box-shadow:
        0 5px 18px
        rgba(0,0,0,.17);
}

.school-financial-report
.student-type-financial-table
.ant-table-tbody > tr:hover > td {
    background:
        #000 !important;

    color:
        #fff !important;
}

.school-financial-report
.student-type-financial-table
.ant-table-tbody > tr:hover
.ant-typography {
    color:
        #fff !important;
}


/* ==========================================================
   BUTTON ANIMATIONS
========================================================== */

.school-financial-report .ant-btn {
    transition:
        transform .22s
        cubic-bezier(.22, 1, .36, 1),
        box-shadow .22s ease,
        background-color .22s ease,
        border-color .22s ease;
}

.school-financial-report .ant-btn:hover {
    transform:
        translateY(-2px);
}

.school-financial-report .ant-btn:active {
    transform:
        translateY(0);
}


/* ==========================================================
   PAGINATION
========================================================== */

.school-financial-report .ant-pagination-item {
    transition:
        transform .2s ease,
        box-shadow .2s ease;
}

.school-financial-report .ant-pagination-item:hover {
    transform:
        translateY(-2px);

    box-shadow:
        0 4px 12px
        rgba(37,99,235,.15);
}

.school-financial-report
.ant-pagination-item-active {
    border-color:
        #4f46e5;
}


/* ==========================================================
   DOWNLOAD BAR
========================================================== */

.school-financial-report
.financial-download-bar {
    backdrop-filter:
        blur(14px);

    -webkit-backdrop-filter:
        blur(14px);
}

.school-financial-report
.financial-download-bar
.ant-card {
    background:
        linear-gradient(
            135deg,
            rgba(255,255,255,.96),
            rgba(239,246,255,.96)
        );

    border:
        1px solid
        rgba(79,70,229,.12);
}


/* ==========================================================
   MOBILE
========================================================== */

@media (max-width: 767px) {

    .school-financial-report {
        overflow-x:
            hidden;
    }

    .school-financial-report
    .ant-card {
        border-radius:
            14px;
    }

    .school-financial-report
    .ant-card-body {
        padding:
            14px;
    }

    /*
     * Important:
     * Tables intentionally retain readable widths.
     * The container scrolls horizontally.
     */

    .school-financial-report
    .table-scroll-mobile {
        width:
            100%;

        max-width:
            100%;

        overflow-x:
            auto;

        overflow-y:
            hidden;

        -webkit-overflow-scrolling:
            touch;

        scroll-behavior:
            smooth;

        scrollbar-width:
            thin;

        scrollbar-color:
            #64748b
            #e5e7eb;

        padding-bottom:
            5px;
    }

    .school-financial-report
    .table-scroll-mobile::-webkit-scrollbar {
        height:
            7px;
    }

    .school-financial-report
    .table-scroll-mobile::-webkit-scrollbar-track {
        background:
            #e5e7eb;

        border-radius:
            20px;
    }

    .school-financial-report
    .table-scroll-mobile::-webkit-scrollbar-thumb {
        background:
            #64748b;

        border-radius:
            20px;
    }

    .school-financial-report
    .grade-financial-table {
        min-width:
            850px;
    }

    .school-financial-report
    .student-type-financial-table {
        min-width:
            760px;
    }

    .school-financial-report
    .expanded-financial-table {
        min-width:
            680px;
    }

    .school-financial-report
    .grade-expanded-panel {
        padding:
            15px !important;

        border-radius:
            0 0 12px 12px;
    }

    .school-financial-report
    .grade-expanded-title {
        font-size:
            15px;

        line-height:
            1.45;
    }

    .school-financial-report
    .ant-table-thead > tr > th,
    .school-financial-report
    .ant-table-tbody > tr > td {
        white-space:
            nowrap;
    }

    .school-financial-report
    .financial-download-bar {
        bottom:
            8px !important;
    }

}


/* ==========================================================
   SMALL MOBILE
========================================================== */

@media (max-width: 480px) {

    .school-financial-report
    .ant-card-body {
        padding:
            12px;
    }

    .school-financial-report
    .grade-expanded-panel {
        padding:
            12px !important;
    }

    .school-financial-report
    .table-scroll-mobile {
        margin-left:
            -1px;

        margin-right:
            -1px;
    }

}


/* ==========================================================
   REDUCE MOTION
========================================================== */

@media (prefers-reduced-motion: reduce) {

    .school-financial-report *,
    .school-financial-report *::before,
    .school-financial-report *::after {

        animation-duration:
            .01ms !important;

        animation-iteration-count:
            1 !important;

        transition-duration:
            .01ms !important;

        scroll-behavior:
            auto !important;
    }

}
`;


const SchoolFinancialReport = () => {

    const {
        selectedYear,
    } = useAcademicYear();

    const [
        report,
        setReport
    ] = useState(null);

    const [
        loading,
        setLoading
    ] = useState(false);

    const [
        downloading,
        setDownloading
    ] = useState(false);

    const [
        error,
        setError
    ] = useState("");

    const [
        selectedGrade,
        setSelectedGrade
    ] = useState(
        "all"
    );

    const [
        selectedStudentType,
        setSelectedStudentType
    ] = useState(
        "all"
    );

    const [
        activeGradePanel,
        setActiveGradePanel
    ] = useState([]);

    const requestIdRef =
        useRef(0);


    /* ======================================================
       VISUAL THEME
    ====================================================== */

    useEffect(
        () => {

            const styleId =
                "school-financial-report-theme";

            if (
                !document.getElementById(
                    styleId
                )
            ) {

                const style =
                    document.createElement(
                        "style"
                    );

                style.id =
                    styleId;

                style.innerHTML =
                    financialReportStyles;

                document.head.appendChild(
                    style
                );

            }

            return () => {

                const style =
                    document.getElementById(
                        styleId
                    );

                if (
                    style
                ) {

                    style.remove();

                }

            };

        },
        []
    );


    /*
     * ======================================================
     * FETCH FINANCIAL REPORT
     * ======================================================
     */

    const fetchReport =
        useCallback(
            async () => {

                if (
                    !selectedYear?.id
                ) {

                    setReport(
                        null
                    );

                    return;

                }

                const requestId =
                    ++requestIdRef.current;

                setLoading(
                    true
                );

                setError(
                    ""
                );

                try {

                    const response =
                        await api.get(
                            API_ENDPOINT,
                            {
                                params: {
                                    academic_year:
                                        selectedYear.id,
                                },
                            }
                        );

                    if (
                        requestId
                        !==
                        requestIdRef.current
                    ) {
                        return;
                    }

                    setReport(
                        normalizeReport(
                            response
                        )
                    );

                } catch (
                    requestError
                ) {

                    console.error(
                        "Financial report error:",
                        requestError
                    );

                    if (
                        requestId
                        !==
                        requestIdRef.current
                    ) {
                        return;
                    }

                    setReport(
                        null
                    );

                    setError(
                        requestError
                            ?.response
                            ?.data
                            ?.detail
                        ||
                        "Unable to load the school financial report."
                    );

                } finally {

                    if (
                        requestId
                        ===
                        requestIdRef.current
                    ) {

                        setLoading(
                            false
                        );

                    }

                }

            },
            [
                selectedYear?.id
            ]
        );


    useEffect(
        () => {

            fetchReport();

        },
        [
            fetchReport
        ]
    );


    /*
     * ======================================================
     * FILTERED GRADE DATA
     * ======================================================
     */

    const gradeData =
        useMemo(
            () => {

                if (
                    !report
                ) {
                    return [];
                }

                let result =
                    report.grade_class_breakdown;

                if (
                    selectedGrade
                    !==
                    "all"
                ) {

                    result =
                        result.filter(
                            grade =>
                                String(
                                    grade.grade_class_id
                                )
                                ===
                                String(
                                    selectedGrade
                                )
                        );

                }

                if (
                    selectedStudentType
                    !==
                    "all"
                ) {

                    result =
                        result.map(
                            grade => {

                                const type =
                                    grade
                                        .student_type_breakdown
                                        ?.[
                                            selectedStudentType
                                        ];

                                if (
                                    !type
                                ) {

                                    return {
                                        ...grade,
                                        total_students:
                                            0,
                                        total_fee:
                                            "0",
                                        total_collected:
                                            "0",
                                        outstanding_balance:
                                            "0",
                                        installment_breakdown:
                                            {},
                                        student_type_breakdown:
                                            {
                                                [selectedStudentType]:
                                                    type
                                            },
                                    };

                                }

                                return {

                                    ...grade,

                                    total_students:
                                        type.student_count,

                                    total_fee:
                                        type.total_fee,

                                    total_collected:
                                        type.total_collected,

                                    outstanding_balance:
                                        type.outstanding_balance,

                                    installment_breakdown:
                                        type.installments,

                                };

                            }
                        );

                }

                return result;

            },
            [
                report,
                selectedGrade,
                selectedStudentType,
            ]
        );


    /*
     * ======================================================
     * GRADE OPTIONS
     * ======================================================
     */

    const gradeOptions =
        useMemo(
            () => [

                {
                    value:
                        "all",
                    label:
                        "All Grade Classes",
                },

                ...(
                    report
                        ?.grade_class_breakdown
                    ||
                    []
                )
                .map(
                    grade => ({
                        value:
                            String(
                                grade.grade_class_id
                            ),
                        label:
                            grade.grade_class,
                    })
                ),

            ],
            [
                report
            ]
        );


    /*
     * ======================================================
     * SUMMARY
     * ======================================================
     */

    const summary =
        report?.summary || {};

    const totalFee =
        Number(
            summary.total_fee || 0
        );

    const totalCollected =
        Number(
            summary.total_collected || 0
        );

    const outstandingBalance =
        Number(
            summary.outstanding_balance || 0
        );

    const collectionRate =
        percentage(
            totalCollected,
            totalFee
        );


    /*
     * ======================================================
     * INSTALLMENT CHART
     * ======================================================
     */

    const installmentChartData =
        useMemo(
            () => {

                return INSTALLMENT_ORDER.map(
                    installment => {

                        const item =
                            report
                                ?.installment_breakdown
                                ?.[
                                    installment
                                ]
                            || {};

                        return {

                            name:
                                INSTALLMENT_LABELS[
                                    installment
                                ],

                            fee:
                                Number(
                                    item.total_fee
                                    || 0
                                ),

                            collected:
                                Number(
                                    item.total_collected
                                    || 0
                                ),

                            outstanding:
                                Number(
                                    item.outstanding_balance
                                    || 0
                                ),

                        };

                    }
                );

            },
            [
                report
            ]
        );


    /*
     * ======================================================
     * STUDENT TYPE CHART
     * ======================================================
     */

    const studentTypeChartData =
        useMemo(
            () => {

                return STUDENT_TYPE_ORDER.map(
                    type => {

                        const item =
                            report
                                ?.student_type_breakdown
                                ?.[
                                    type
                                ]
                            || {};

                        return {

                            name:
                                STUDENT_TYPE_LABELS[
                                    type
                                ],

                            students:
                                Number(
                                    item.student_count
                                    || 0
                                ),

                            fee:
                                Number(
                                    item.total_fee
                                    || 0
                                ),

                            collected:
                                Number(
                                    item.total_collected
                                    || 0
                                ),

                            outstanding:
                                Number(
                                    item.outstanding_balance
                                    || 0
                                ),

                        };

                    }
                )
                .filter(
                    item =>
                        item.students > 0
                        ||
                        item.fee > 0
                        ||
                        item.collected > 0
                );

            },
            [
                report
            ]
        );


    /*
     * ======================================================
     * GRADE CHART
     * ======================================================
     */

    const gradeChartData =
        useMemo(
            () => {

                return (
                    gradeData
                    ||
                    []
                )
                .map(
                    grade => ({

                        name:
                            grade.grade_class,

                        fee:
                            Number(
                                grade.total_fee
                                || 0
                            ),

                        collected:
                            Number(
                                grade.total_collected
                                || 0
                            ),

                        outstanding:
                            Number(
                                grade.outstanding_balance
                                || 0
                            ),

                    })
                );

            },
            [
                gradeData
            ]
        );


    /*
     * ======================================================
     * COLLECTION TREND
     * ======================================================
     */

    const collectionTrend =
        useMemo(
            () => {

                return installmentChartData.map(
                    item => ({

                        name:
                            item.name,

                        collected:
                            item.collected,

                        outstanding:
                            item.outstanding,

                    })
                );

            },
            [
                installmentChartData
            ]
        );


    /*
     * ======================================================
     * DOWNLOAD ONE PDF
     * ======================================================
     */

    const downloadPDF =
        useCallback(
            async (
                documentComponent,
                filename
            ) => {

                const blob =
                    await pdf(
                        documentComponent
                    ).toBlob();

                downloadBlob(
                    blob,
                    filename
                );

            },
            []
        );


    /*
     * ======================================================
     * DOWNLOAD EXECUTIVE PDF
     * ======================================================
     */

    const handleDownloadExecutive =
        useCallback(
            async () => {

                if (
                    !report
                ) {
                    return;
                }

                setDownloading(
                    true
                );

                try {

                    await downloadPDF(

                        <ExecutiveFinancialReportPDF
                            report={
                                report
                            }
                            selectedYear={
                                selectedYear
                            }
                        />,

                        `School-Financial-Executive-${selectedYear?.name || "Report"}.pdf`

                    );

                    message.success(
                        "Executive financial report downloaded."
                    );

                } catch (
                    downloadError
                ) {

                    console.error(
                        downloadError
                    );

                    message.error(
                        "Failed to generate the executive PDF."
                    );

                } finally {

                    setDownloading(
                        false
                    );

                }

            },
            [
                report,
                selectedYear,
                downloadPDF
            ]
        );


    /*
     * ======================================================
     * DOWNLOAD GRADE PDF
     * ======================================================
     */

    const handleDownloadGrades =
        useCallback(
            async () => {

                if (
                    !report
                ) {
                    return;
                }

                setDownloading(
                    true
                );

                try {

                    await downloadPDF(

                        <GradeFinancialReportPDF
                            report={
                                report
                            }
                            gradeData={
                                gradeData
                            }
                            selectedYear={
                                selectedYear
                            }
                        />,

                        `School-Financial-Grade-Classes-${selectedYear?.name || "Report"}.pdf`

                    );

                    message.success(
                        "Grade-class financial report downloaded."
                    );

                } catch (
                    downloadError
                ) {

                    console.error(
                        downloadError
                    );

                    message.error(
                        "Failed to generate the grade PDF."
                    );

                } finally {

                    setDownloading(
                        false
                    );

                }

            },
            [
                report,
                gradeData,
                selectedYear,
                downloadPDF
            ]
        );


    /*
     * ======================================================
     * DOWNLOAD STUDENT TYPE PDF
     * ======================================================
     */

    const handleDownloadStudentTypes =
        useCallback(
            async () => {

                if (
                    !report
                ) {
                    return;
                }

                setDownloading(
                    true
                );

                try {

                    await downloadPDF(

                        <StudentTypeFinancialReportPDF
                            report={
                                report
                            }
                            selectedYear={
                                selectedYear
                            }
                        />,

                        `School-Financial-Student-Types-${selectedYear?.name || "Report"}.pdf`

                    );

                    message.success(
                        "Student-type financial report downloaded."
                    );

                } catch (
                    downloadError
                ) {

                    console.error(
                        downloadError
                    );

                    message.error(
                        "Failed to generate the student-type PDF."
                    );

                } finally {

                    setDownloading(
                        false
                    );

                }

            },
            [
                report,
                selectedYear,
                downloadPDF
            ]
        );


    /*
     * ======================================================
     * DOWNLOAD ALL PDF REPORTS
     * ======================================================
     */

    const handleDownloadAll =
        useCallback(
            async () => {

                if (
                    !report
                ) {
                    return;
                }

                setDownloading(
                    true
                );

                try {

                    const yearName =
                        selectedYear?.name
                        ||
                        report
                            ?.academic_year
                            ?.name
                        ||
                        "Report";


                    const executiveBlob =
                        await pdf(

                            <ExecutiveFinancialReportPDF
                                report={
                                    report
                                }
                                selectedYear={
                                    selectedYear
                                }
                            />

                        ).toBlob();

                    downloadBlob(
                        executiveBlob,
                        `School-Financial-Executive-${yearName}.pdf`
                    );


                    await new Promise(
                        resolve =>
                            setTimeout(
                                resolve,
                                350
                            )
                    );


                    const gradeBlob =
                        await pdf(

                            <GradeFinancialReportPDF
                                report={
                                    report
                                }
                                gradeData={
                                    gradeData
                                }
                                selectedYear={
                                    selectedYear
                                }
                            />

                        ).toBlob();

                    downloadBlob(
                        gradeBlob,
                        `School-Financial-Grade-Classes-${yearName}.pdf`
                    );


                    await new Promise(
                        resolve =>
                            setTimeout(
                                resolve,
                                350
                            )
                    );


                    const studentTypeBlob =
                        await pdf(

                            <StudentTypeFinancialReportPDF
                                report={
                                    report
                                }
                                selectedYear={
                                    selectedYear
                                }
                            />

                        ).toBlob();

                    downloadBlob(
                        studentTypeBlob,
                        `School-Financial-Student-Types-${yearName}.pdf`
                    );


                    message.success(
                        "All three financial PDF reports have been generated."
                    );

                } catch (
                    downloadError
                ) {

                    console.error(
                        "PDF generation error:",
                        downloadError
                    );

                    message.error(
                        "One or more PDF reports could not be generated."
                    );

                } finally {

                    setDownloading(
                        false
                    );

                }

            },
            [
                report,
                gradeData,
                selectedYear
            ]
        );


    /*
     * ======================================================
     * GRADE TABLE COLUMNS
     * ======================================================
     */

    const gradeColumns =
        useMemo(
            () => [

                {
                    title:
                        "Grade Class",

                    dataIndex:
                        "grade_class",

                    key:
                        "grade_class",

                    fixed:
                        "left",

                    width:
                        150,

                    render:
                        value => (

                            <Text strong>
                                {value}
                            </Text>

                        ),

                },

                {
                    title:
                        "Students",

                    dataIndex:
                        "total_students",

                    key:
                        "students",

                    align:
                        "center",

                    width:
                        100,

                    render:
                        value =>
                            formatNumber(
                                value
                            ),

                },

                {
                    title:
                        "Total Fee",

                    dataIndex:
                        "total_fee",

                    key:
                        "total_fee",

                    align:
                        "right",

                    width:
                        145,

                    render:
                        value =>
                            formatMoney(
                                value
                            ),

                },

                {
                    title:
                        "Collected",

                    dataIndex:
                        "total_collected",

                    key:
                        "total_collected",

                    align:
                        "right",

                    width:
                        145,

                    render:
                        value => (

                            <Text
                                type="success"
                                strong
                            >
                                {formatMoney(
                                    value
                                )}
                            </Text>

                        ),

                },

                {
                    title:
                        "Outstanding",

                    dataIndex:
                        "outstanding_balance",

                    key:
                        "outstanding_balance",

                    align:
                        "right",

                    width:
                        145,

                    render:
                        value => (

                            <Text
                                type="danger"
                                strong
                            >
                                {formatMoney(
                                    value
                                )}
                            </Text>

                        ),

                },

                {
                    title:
                        "Collection",

                    key:
                        "collection",

                    width:
                        150,

                    render:
                        (_, record) => {

                            const rate =
                                percentage(
                                    record.total_collected,
                                    record.total_fee
                                );

                            return (

                                <Tag
                                    color={
                                        getCollectionColor(
                                            record.total_collected,
                                            record.total_fee
                                        )
                                    }
                                >
                                    {rate.toFixed(1)}%
                                </Tag>

                            );

                        },

                },

            ],
            []
        );


    /*
     * ======================================================
     * GRADE EXPANDED ROW
     * ======================================================
     */

    const renderGradeExpanded =
        useCallback(
            record => {

                const typeRows =
                    STUDENT_TYPE_ORDER.map(
                        type => {

                            const item =
                                record
                                    .student_type_breakdown
                                    ?.[
                                        type
                                    ]
                                || {};

                            return {

                                key:
                                    type,

                                studentType:
                                    STUDENT_TYPE_LABELS[
                                        type
                                    ],

                                students:
                                    Number(
                                        item.student_count
                                        || 0
                                    ),

                                fee:
                                    Number(
                                        item.total_fee
                                        || 0
                                    ),

                                collected:
                                    Number(
                                        item.total_collected
                                        || 0
                                    ),

                                outstanding:
                                    Number(
                                        item.outstanding_balance
                                        || 0
                                    ),

                            };

                        }
                    )
                    .filter(
                        item =>
                            item.students > 0
                            ||
                            item.fee > 0
                    );


                return (

                    <div
                        className="grade-expanded-panel"
                    >

                        <div
                            className="grade-expanded-title"
                        >
                            Financial Breakdown —{" "}
                            {record.grade_class}
                        </div>


                        <div
                            className="expanded-table-container"
                        >

                            <Table
                                className="
                                    expanded-financial-table
                                    expanded-student-table
                                "
                                size="small"
                                pagination={false}
                                dataSource={
                                    typeRows
                                }
                                columns={[
                                    {
                                        title:
                                            "Student Type",

                                        dataIndex:
                                            "studentType",

                                        key:
                                            "studentType",
                                    },

                                    {
                                        title:
                                            "Students",

                                        dataIndex:
                                            "students",

                                        key:
                                            "students",

                                        align:
                                            "center",
                                    },

                                    {
                                        title:
                                            "Total Fee",

                                        dataIndex:
                                            "fee",

                                        key:
                                            "fee",

                                        align:
                                            "right",

                                        render:
                                            value =>
                                                formatMoney(
                                                    value
                                                ),
                                    },

                                    {
                                        title:
                                            "Collected",

                                        dataIndex:
                                            "collected",

                                        key:
                                            "collected",

                                        align:
                                            "right",

                                        render:
                                            value =>
                                                formatMoney(
                                                    value
                                                ),
                                    },

                                    {
                                        title:
                                            "Outstanding",

                                        dataIndex:
                                            "outstanding",

                                        key:
                                            "outstanding",

                                        align:
                                            "right",

                                        render:
                                            value =>
                                                formatMoney(
                                                    value
                                                ),
                                    },

                                ]}
                            />

                        </div>


                        <div
                            className="expanded-table-container"
                        >

                            <Table
                                className="
                                    expanded-financial-table
                                    expanded-installment-table
                                "
                                size="small"
                                pagination={false}
                                dataSource={
                                    INSTALLMENT_ORDER.map(
                                        installment => {

                                            const item =
                                                record
                                                    .installment_breakdown
                                                    ?.[
                                                        installment
                                                    ]
                                                || {};

                                            return {

                                                key:
                                                    installment,

                                                installment:
                                                    INSTALLMENT_LABELS[
                                                        installment
                                                    ],

                                                fee:
                                                    Number(
                                                        item.total_fee
                                                        || 0
                                                    ),

                                                collected:
                                                    Number(
                                                        item.total_collected
                                                        || 0
                                                    ),

                                                outstanding:
                                                    Number(
                                                        item.outstanding_balance
                                                        || 0
                                                    ),

                                            };

                                        }
                                    )
                                }
                                columns={[
                                    {
                                        title:
                                            "Installment",

                                        dataIndex:
                                            "installment",

                                        key:
                                            "installment",
                                    },

                                    {
                                        title:
                                            "Total Fee",

                                        dataIndex:
                                            "fee",

                                        key:
                                            "fee",

                                        align:
                                            "right",

                                        render:
                                            value =>
                                                formatMoney(
                                                    value
                                                ),
                                    },

                                    {
                                        title:
                                            "Collected",

                                        dataIndex:
                                            "collected",

                                        key:
                                            "collected",

                                        align:
                                            "right",

                                        render:
                                            value =>
                                                formatMoney(
                                                    value
                                                ),
                                    },

                                    {
                                        title:
                                            "Outstanding",

                                        dataIndex:
                                            "outstanding",

                                        key:
                                            "outstanding",

                                        align:
                                            "right",

                                        render:
                                            value =>
                                                formatMoney(
                                                    value
                                                ),
                                    },

                                ]}
                            />

                        </div>

                    </div>

                );

            },
            []
        );


    /*
     * ======================================================
     * NO YEAR
     * ======================================================
     */

    if (
        !selectedYear?.id
    ) {

        return (

            <div
                style={{
                    padding:
                        24,
                }}
            >

                <Empty
                    description={
                        "Select an academic year to view the financial report."
                    }
                />

            </div>

        );

    }


    /*
     * ======================================================
     * LOADING
     * ======================================================
     */

    if (
        loading
        &&
        !report
    ) {

        return (

            <div
                style={{
                    minHeight:
                        "60vh",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",
                }}
            >

                <Space
                    direction="vertical"
                    align="center"
                >

                    <Spin
                        size="large"
                    />

                    <Text type="secondary">
                        Building financial intelligence...
                    </Text>

                </Space>

            </div>

        );

    }


    /*
     * ======================================================
     * ERROR
     * ======================================================
     */

    if (
        error
        &&
        !report
    ) {

        return (

            <div
                style={{
                    padding:
                        24,
                }}
            >

                <Alert
                    type="error"
                    showIcon
                    message="Financial Report Error"
                    description={
                        error
                    }
                    action={

                        <Button
                            icon={
                                <ReloadOutlined />
                            }
                            onClick={
                                fetchReport
                            }
                        >
                            Retry
                        </Button>

                    }
                />

            </div>

        );

    }


    return (

        <div
            className="school-financial-report"
            style={{
                width:
                    "100%",

                minHeight:
                    "100vh",

                background:
                    "#f4f7fb",

                padding:
                    "clamp(12px, 2vw, 28px)",

                boxSizing:
                    "border-box",
            }}
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div
                className="financial-main-header"
                style={{
                    borderRadius:
                        20,

                    padding:
                        "clamp(18px, 3vw, 30px)",

                    marginBottom:
                        18,

                    color:
                        "#fff",
                }}
            >

                <Row
                    gutter={[
                        16,
                        18
                    ]}
                    align="middle"
                    justify="space-between"
                >

                    <Col
                        xs={24}
                        lg={14}
                    >

                        <Space
                            direction="vertical"
                            size={4}
                        >

                            <Tag
                                color="gold"
                            >
                                FINANCIAL INTELLIGENCE
                            </Tag>

                            <Title
                                level={2}
                                style={{
                                    color:
                                        "#fff",

                                    margin:
                                        0,

                                    fontSize:
                                        "clamp(22px, 4vw, 34px)",
                                }}
                            >
                                School Financial Report
                            </Title>

                            <Text
                                style={{
                                    color:
                                        "rgba(255,255,255,.72)",
                                }}
                            >
                                Academic Year:{" "}
                                <strong>
                                    {
                                        selectedYear.name
                                        ||
                                        report
                                            ?.academic_year
                                            ?.name
                                    }
                                </strong>
                            </Text>

                        </Space>

                    </Col>


                    <Col
                        xs={24}
                        lg={10}
                    >

                        <div
                            style={{
                                display:
                                    "flex",

                                flexWrap:
                                    "wrap",

                                gap:
                                    8,

                                justifyContent:
                                    "flex-end",
                            }}
                        >

                            <Tooltip
                                title="Refresh report"
                            >

                                <Button
                                    icon={
                                        <ReloadOutlined />
                                    }
                                    loading={
                                        loading
                                    }
                                    onClick={
                                        fetchReport
                                    }
                                >
                                    Refresh
                                </Button>

                            </Tooltip>


                            <Button
                                icon={
                                    <FilePdfOutlined />
                                }
                                onClick={
                                    handleDownloadExecutive
                                }
                                loading={
                                    downloading
                                }
                            >
                                Executive PDF
                            </Button>


                            <Button
                                icon={
                                    <CloudDownloadOutlined />
                                }
                                type="primary"
                                onClick={
                                    handleDownloadAll
                                }
                                loading={
                                    downloading
                                }
                            >
                                Download Full Report
                            </Button>

                        </div>

                    </Col>

                </Row>

            </div>


            {/* =================================================
                ERROR ALERT
            ================================================= */}

            {
                error
                &&
                report
                &&
                (

                    <Alert
                        style={{
                            marginBottom:
                                18,
                        }}
                        type="warning"
                        showIcon
                        message={
                            error
                        }
                    />

                )
            }


            {/* =================================================
                KPI CARDS
            ================================================= */}

            <Row
                gutter={[
                    14,
                    14
                ]}
            >

                <Col
                    xs={24}
                    sm={12}
                    xl={6}
                >

                    <Card
                        className="
                            financial-kpi-card
                            kpi-students
                        "
                        bordered={false}
                        style={{
                            height:
                                "100%",

                            borderRadius:
                                16,
                        }}
                    >

                        <Statistic
                            title="Total Students"
                            value={
                                summary.total_students
                                || 0
                            }
                            prefix={
                                <TeamOutlined />
                            }
                        />

                        <Text>
                            Enrolled students with fee obligations
                        </Text>

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    xl={6}
                >

                    <Card
                        className="
                            financial-kpi-card
                            kpi-fees
                        "
                        bordered={false}
                        style={{
                            height:
                                "100%",

                            borderRadius:
                                16,
                        }}
                    >

                        <Statistic
                            title="Total Fees"
                            value={
                                totalFee
                            }
                            precision={2}
                            prefix={
                                <BankOutlined />
                            }
                            formatter={
                                value =>
                                    formatMoney(
                                        value
                                    )
                            }
                        />

                        <Text>
                            Total expected financial obligation
                        </Text>

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    xl={6}
                >

                    <Card
                        className="
                            financial-kpi-card
                            kpi-collected
                        "
                        bordered={false}
                        style={{
                            height:
                                "100%",

                            borderRadius:
                                16,
                        }}
                    >

                        <Statistic
                            title="Total Collected"
                            value={
                                totalCollected
                            }
                            precision={2}
                            prefix={
                                <CheckCircleOutlined />
                            }
                            formatter={
                                value =>
                                    formatMoney(
                                        value
                                    )
                            }
                        />

                        <Text>
                            Money successfully collected
                        </Text>

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    xl={6}
                >

                    <Card
                        className="
                            financial-kpi-card
                            kpi-outstanding
                        "
                        bordered={false}
                        style={{
                            height:
                                "100%",

                            borderRadius:
                                16,
                        }}
                    >

                        <Statistic
                            title="Outstanding Balance"
                            value={
                                outstandingBalance
                            }
                            precision={2}
                            prefix={
                                <WarningOutlined />
                            }
                            formatter={
                                value =>
                                    formatMoney(
                                        value
                                    )
                            }
                        />

                        <Text>
                            Remaining amount to collect
                        </Text>

                    </Card>

                </Col>

            </Row>


            {/* =================================================
                COLLECTION HEALTH
            ================================================= */}

            <Row
                gutter={[
                    14,
                    14
                ]}
                style={{
                    marginTop:
                        14,
                }}
            >

                <Col
                    xs={24}
                    lg={8}
                >

                    <Card
                        className="
                            report-section-card
                            section-health
                            collection-health-card
                        "
                        bordered={false}
                        style={{
                            borderRadius:
                                16,

                            height:
                                "100%",
                        }}
                    >

                        <Space
                            direction="vertical"
                            style={{
                                width:
                                    "100%",
                            }}
                        >

                            <Space
                                align="center"
                            >

                                <RiseOutlined />

                                <Text strong>
                                    Collection Health
                                </Text>

                            </Space>


                            <Title
                                level={1}
                                style={{
                                    margin:
                                        "8px 0",
                                }}
                            >
                                {collectionRate.toFixed(1)}%
                            </Title>


                            <Text type="secondary">
                                {formatMoney(
                                    totalCollected
                                )} collected from{" "}
                                {formatMoney(
                                    totalFee
                                )}
                            </Text>


                            <div
                                style={{
                                    width:
                                        "100%",

                                    height:
                                        10,

                                    borderRadius:
                                        20,

                                    background:
                                        "#e5e7eb",

                                    overflow:
                                        "hidden",
                                }}
                            >

                                <div
                                    style={{
                                        width:
                                            `${collectionRate}%`,

                                        height:
                                            "100%",

                                        background:
                                            collectionRate >= 90
                                                ? "#16a34a"
                                                : collectionRate >= 70
                                                    ? "#2563eb"
                                                    : collectionRate >= 40
                                                        ? "#d97706"
                                                        : "#dc2626",

                                        transition:
                                            "width .5s ease",
                                    }}
                                />

                            </div>

                        </Space>

                    </Card>

                </Col>


                <Col
                    xs={24}
                    lg={16}
                >

                    <Card
                        className="
                            report-section-card
                            section-position
                            financial-position-card
                        "
                        bordered={false}
                        style={{
                            borderRadius:
                                16,

                            height:
                                "100%",
                        }}
                        title={
                            <Space>
                                <DollarOutlined />
                                <span>
                                    Financial Position
                                </span>
                            </Space>
                        }
                    >

                        <Row
                            gutter={[
                                12,
                                12
                            ]}
                        >

                            <Col
                                xs={24}
                                sm={8}
                            >

                                <Statistic
                                    title="Expected"
                                    value={
                                        totalFee
                                    }
                                    precision={2}
                                    formatter={
                                        value =>
                                            formatMoney(
                                                value
                                            )
                                    }
                                />

                            </Col>


                            <Col
                                xs={24}
                                sm={8}
                            >

                                <Statistic
                                    title="Collected"
                                    value={
                                        totalCollected
                                    }
                                    precision={2}
                                    valueStyle={{
                                        color:
                                            "#16a34a",
                                    }}
                                    formatter={
                                        value =>
                                            formatMoney(
                                                value
                                            )
                                    }
                                />

                            </Col>


                            <Col
                                xs={24}
                                sm={8}
                            >

                                <Statistic
                                    title="Outstanding"
                                    value={
                                        outstandingBalance
                                    }
                                    precision={2}
                                    valueStyle={{
                                        color:
                                            "#dc2626",
                                    }}
                                    formatter={
                                        value =>
                                            formatMoney(
                                                value
                                            )
                                    }
                                />

                            </Col>

                        </Row>

                    </Card>

                </Col>

            </Row>


            {/* =================================================
                FILTERS
            ================================================= */}

            <Card
                className="
                    report-section-card
                    section-filters
                    financial-filter-card
                "
                bordered={false}
                style={{
                    marginTop:
                        14,

                    borderRadius:
                        16,
                }}
            >

                <Row
                    gutter={[
                        12,
                        12
                    ]}
                    align="middle"
                >

                    <Col
                        xs={24}
                        sm={12}
                        md={8}
                    >

                        <Text strong>
                            Grade Class
                        </Text>

                        <Select
                            style={{
                                width:
                                    "100%",

                                marginTop:
                                    6,
                            }}
                            value={
                                selectedGrade
                            }
                            options={
                                gradeOptions
                            }
                            onChange={
                                setSelectedGrade
                            }
                            showSearch
                            optionFilterProp="label"
                        />

                    </Col>


                    <Col
                        xs={24}
                        sm={12}
                        md={8}
                    >

                        <Text strong>
                            Student Type
                        </Text>

                        <Select
                            style={{
                                width:
                                    "100%",

                                marginTop:
                                    6,
                            }}
                            value={
                                selectedStudentType
                            }
                            onChange={
                                setSelectedStudentType
                            }
                            options={[
                                {
                                    value:
                                        "all",

                                    label:
                                        "All Student Types",
                                },

                                ...STUDENT_TYPE_ORDER.map(
                                    type => ({
                                        value:
                                            type,

                                        label:
                                            STUDENT_TYPE_LABELS[
                                                type
                                            ],
                                    })
                                ),
                            ]}
                        />

                    </Col>


                    <Col
                        xs={24}
                        sm={12}
                        md={8}
                    >

                        <Text strong>
                            Report Downloads
                        </Text>

                        <div
                            style={{
                                marginTop:
                                    6,

                                display:
                                    "flex",

                                gap:
                                    8,

                                flexWrap:
                                    "wrap",
                            }}
                        >

                            <Button
                                icon={
                                    <FilePdfOutlined />
                                }
                                onClick={
                                    handleDownloadGrades
                                }
                                loading={
                                    downloading
                                }
                            >
                                Grade PDF
                            </Button>


                            <Button
                                icon={
                                    <FilePdfOutlined />
                                }
                                onClick={
                                    handleDownloadStudentTypes
                                }
                                loading={
                                    downloading
                                }
                            >
                                Student Types PDF
                            </Button>

                        </div>

                    </Col>

                </Row>

            </Card>


            {/* =================================================
                INSTALLMENT BAR CHART
            ================================================= */}

            <Row
                gutter={[
                    14,
                    14
                ]}
                style={{
                    marginTop:
                        14,
                }}
            >

                <Col
                    xs={24}
                    xl={14}
                >

                    <Card
                        className="
                            chart-card
                            installment-chart-card
                            report-section-card
                            section-installments
                        "
                        bordered={false}
                        style={{
                            borderRadius:
                                16,
                        }}
                        title={
                            <Space>
                                <BarChartOutlined />
                                <span>
                                    Installment Collection Analysis
                                </span>
                            </Space>
                        }
                    >

                        <div
                            style={{
                                width:
                                    "100%",

                                height:
                                    360,
                            }}
                        >

                            <ResponsiveContainer
                                width="100%"
                                height="100%"
                            >

                                <BarChart
                                    data={
                                        installmentChartData
                                    }
                                    margin={{
                                        top:
                                            10,

                                        right:
                                            10,

                                        left:
                                            10,

                                        bottom:
                                            55,
                                    }}
                                >

                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                    />

                                    <XAxis
                                        dataKey="name"
                                        angle={-25}
                                        textAnchor="end"
                                        interval={0}
                                        tick={{
                                            fontSize:
                                                11,
                                        }}
                                    />

                                    <YAxis
                                        tick={{
                                            fontSize:
                                                11,
                                        }}
                                        tickFormatter={
                                            value =>
                                                formatMoney(
                                                    value
                                                )
                                        }
                                    />

                                    <RechartsTooltip
                                        formatter={
                                            value =>
                                                formatMoney(
                                                    value
                                                )
                                        }
                                    />

                                    <Legend />

                                    <Bar
                                        dataKey="fee"
                                        name="Total Fee"
                                        fill="#334155"
                                        radius={[
                                            6,
                                            6,
                                            0,
                                            0
                                        ]}
                                    />

                                    <Bar
                                        dataKey="collected"
                                        name="Collected"
                                        fill="#16a34a"
                                        radius={[
                                            6,
                                            6,
                                            0,
                                            0
                                        ]}
                                    />

                                    <Bar
                                        dataKey="outstanding"
                                        name="Outstanding"
                                        fill="#dc2626"
                                        radius={[
                                            6,
                                            6,
                                            0,
                                            0
                                        ]}
                                    />

                                </BarChart>

                            </ResponsiveContainer>

                        </div>

                    </Card>

                </Col>


                {/* =================================================
                    STUDENT TYPE PIE
                ================================================= */}

                <Col
                    xs={24}
                    xl={10}
                >

                    <Card
                        className="
                            chart-card
                            student-chart-card
                            report-section-card
                        "
                        bordered={false}
                        style={{
                            borderRadius:
                                16,
                        }}
                        title={
                            <Space>
                                <PieChartOutlined />
                                <span>
                                    Student Type Distribution
                                </span>
                            </Space>
                        }
                    >

                        <div
                            style={{
                                width:
                                    "100%",

                                height:
                                    360,
                            }}
                        >

                            {
                                studentTypeChartData.length
                                    ? (

                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >

                                            <PieChart>

                                                <Pie
                                                    data={
                                                        studentTypeChartData
                                                    }
                                                    dataKey="students"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius="68%"
                                                    label
                                                >

                                                    {
                                                        studentTypeChartData.map(
                                                            (
                                                                entry,
                                                                index
                                                            ) => (

                                                                <Cell
                                                                    key={
                                                                        `${entry.name}-${index}`
                                                                    }
                                                                    fill={[
                                                                        "#334155",
                                                                        "#2563eb",
                                                                        "#7c3aed",
                                                                        "#d97706",
                                                                    ][
                                                                        index
                                                                        %
                                                                        4
                                                                    ]}
                                                                />

                                                            )
                                                        )
                                                    }

                                                </Pie>

                                                <RechartsTooltip />

                                                <Legend />

                                            </PieChart>

                                        </ResponsiveContainer>

                                    )
                                    : (

                                        <Empty
                                            description={
                                                "No student-type data"
                                            }
                                        />

                                    )
                            }

                        </div>

                    </Card>

                </Col>

            </Row>


            {/* =================================================
                COLLECTION PROGRESSION
            ================================================= */}

            <Card
                className="
                    chart-card
                    progression-chart-card
                    report-section-card
                    section-progression
                "
                bordered={false}
                style={{
                    marginTop:
                        14,

                    borderRadius:
                        16,
                }}
                title={
                    <Space>
                        <RiseOutlined />
                        <span>
                            Collection Progression by Installment
                        </span>
                    </Space>
                }
            >

                <div
                    style={{
                        width:
                            "100%",

                        height:
                            330,
                    }}
                >

                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                    >

                        <LineChart
                            data={
                                collectionTrend
                            }
                            margin={{
                                top:
                                    10,

                                right:
                                    20,

                                left:
                                    10,

                                bottom:
                                    20,
                            }}
                        >

                            <CartesianGrid
                                strokeDasharray="3 3"
                            />

                            <XAxis
                                dataKey="name"
                                tick={{
                                    fontSize:
                                        11,
                                }}
                            />

                            <YAxis
                                tick={{
                                    fontSize:
                                        11,
                                }}
                                tickFormatter={
                                    value =>
                                        formatMoney(
                                            value
                                        )
                                }
                            />

                            <RechartsTooltip
                                formatter={
                                    value =>
                                        formatMoney(
                                            value
                                        )
                                }
                            />

                            <Legend />

                            <Line
                                type="monotone"
                                dataKey="collected"
                                name="Collected"
                                stroke="#16a34a"
                                strokeWidth={3}
                                dot={{
                                    r:
                                        4,
                                }}
                            />

                            <Line
                                type="monotone"
                                dataKey="outstanding"
                                name="Outstanding"
                                stroke="#dc2626"
                                strokeWidth={3}
                                dot={{
                                    r:
                                        4,
                                }}
                            />

                        </LineChart>

                    </ResponsiveContainer>

                </div>

            </Card>


            {/* =================================================
                GRADE COMPARISON
            ================================================= */}

            <Card
                className="
                    chart-card
                    grade-chart-card
                    report-section-card
                    section-grade-chart
                "
                bordered={false}
                style={{
                    marginTop:
                        14,

                    borderRadius:
                        16,
                }}
                title={
                    <Space>
                        <BarChartOutlined />
                        <span>
                            Grade-Class Financial Performance
                        </span>
                    </Space>
                }
            >

                <div
                    style={{
                        width:
                            "100%",

                        height:
                            390,
                    }}
                >

                    {
                        gradeChartData.length
                            ? (

                                <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                >

                                    <BarChart
                                        data={
                                            gradeChartData
                                        }
                                        margin={{
                                            top:
                                                10,

                                            right:
                                                20,

                                            left:
                                                10,

                                            bottom:
                                                70,
                                        }}
                                    >

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                        />

                                        <XAxis
                                            dataKey="name"
                                            angle={-35}
                                            textAnchor="end"
                                            interval={0}
                                            tick={{
                                                fontSize:
                                                    11,
                                            }}
                                        />

                                        <YAxis
                                            tick={{
                                                fontSize:
                                                    11,
                                            }}
                                            tickFormatter={
                                                value =>
                                                    formatMoney(
                                                        value
                                                    )
                                            }
                                        />

                                        <RechartsTooltip
                                            formatter={
                                                value =>
                                                    formatMoney(
                                                        value
                                                    )
                                            }
                                        />

                                        <Legend />

                                        <Bar
                                            dataKey="fee"
                                            name="Total Fee"
                                            fill="#334155"
                                        />

                                        <Bar
                                            dataKey="collected"
                                            name="Collected"
                                            fill="#16a34a"
                                        />

                                        <Bar
                                            dataKey="outstanding"
                                            name="Outstanding"
                                            fill="#dc2626"
                                        />

                                    </BarChart>

                                </ResponsiveContainer>

                            )
                            : (

                                <Empty />

                            )
                    }

                </div>

            </Card>


            {/* =================================================
                GRADE TABLE
            ================================================= */}

            <Card
                className="
                    report-section-card
                    section-grade-table
                "
                bordered={false}
                style={{
                    marginTop:
                        14,

                    borderRadius:
                        16,
                }}
                title={
                    <Space>
                        <BankOutlined />
                        <span>
                            Detailed Grade-Class Financial Records
                        </span>
                    </Space>
                }
            >

                <div
                    className="table-scroll-mobile"
                >

                    <Table
                        className="grade-financial-table"
                        rowKey={
                            "grade_class_id"
                        }
                        columns={
                            gradeColumns
                        }
                        dataSource={
                            gradeData
                        }
                        loading={
                            loading
                        }
                        expandable={{
                            expandedRowRender:
                                renderGradeExpanded,

                            expandedRowKeys:
                                activeGradePanel,

                            onExpandedRowsChange:
                                rows =>
                                    setActiveGradePanel(
                                        rows
                                    ),
                        }}
                        pagination={{
                            pageSize:
                                10,

                            showSizeChanger:
                                true,

                            pageSizeOptions:
                                [
                                    "10",
                                    "20",
                                    "50",
                                    "100",
                                ],

                            showTotal:
                                total =>
                                    `${total} grade classes`,
                        }}
                        scroll={{
                            x:
                                850,
                        }}
                        locale={{
                            emptyText:
                                <Empty
                                    description={
                                        "No financial records found."
                                    }
                                />,
                        }}
                    />

                </div>

            </Card>


            {/* =================================================
                STUDENT TYPE TABLE
            ================================================= */}

            <Card
                className="
                    report-section-card
                    section-student-table
                "
                bordered={false}
                style={{
                    marginTop:
                        14,

                    borderRadius:
                        16,
                }}
                title={
                    <Space>
                        <TeamOutlined />
                        <span>
                            Student-Type Financial Analysis
                        </span>
                    </Space>
                }
            >

                <div
                    className="table-scroll-mobile"
                >

                    <Table
                        className="student-type-financial-table"
                        rowKey={
                            "studentType"
                        }
                        pagination={false}
                        dataSource={
                            STUDENT_TYPE_ORDER
                                .map(
                                    type => {

                                        const item =
                                            report
                                                ?.student_type_breakdown
                                                ?.[
                                                    type
                                                ]
                                            || {};

                                        return {

                                            key:
                                                type,

                                            studentType:
                                                STUDENT_TYPE_LABELS[
                                                    type
                                                ],

                                            students:
                                                Number(
                                                    item.student_count
                                                    || 0
                                                ),

                                            fee:
                                                Number(
                                                    item.total_fee
                                                    || 0
                                                ),

                                            collected:
                                                Number(
                                                    item.total_collected
                                                    || 0
                                                ),

                                            outstanding:
                                                Number(
                                                    item.outstanding_balance
                                                    || 0
                                                ),

                                        };

                                    }
                                )
                                .filter(
                                    item =>
                                        item.students > 0
                                        ||
                                        item.fee > 0
                                )
                        }
                        columns={[
                            {
                                title:
                                    "Student Type",

                                dataIndex:
                                    "studentType",

                                key:
                                    "studentType",
                            },

                            {
                                title:
                                    "Students",

                                dataIndex:
                                    "students",

                                key:
                                    "students",

                                align:
                                    "center",

                                render:
                                    value =>
                                        formatNumber(
                                            value
                                        ),
                            },

                            {
                                title:
                                    "Total Fee",

                                dataIndex:
                                    "fee",

                                key:
                                    "fee",

                                align:
                                    "right",

                                render:
                                    value =>
                                        formatMoney(
                                            value
                                        ),
                            },

                            {
                                title:
                                    "Collected",

                                dataIndex:
                                    "collected",

                                key:
                                    "collected",

                                align:
                                    "right",

                                render:
                                    value => (

                                        <Text
                                            type="success"
                                            strong
                                        >
                                            {formatMoney(
                                                value
                                            )}
                                        </Text>

                                    ),
                            },

                            {
                                title:
                                    "Outstanding",

                                dataIndex:
                                    "outstanding",

                                key:
                                    "outstanding",

                                align:
                                    "right",

                                render:
                                    value => (

                                        <Text
                                            type="danger"
                                            strong
                                        >
                                            {formatMoney(
                                                value
                                            )}
                                        </Text>

                                    ),
                            },

                            {
                                title:
                                    "Collection Rate",

                                key:
                                    "rate",

                                render:
                                    (_, record) => {

                                        const rate =
                                            percentage(
                                                record.collected,
                                                record.fee
                                            );

                                        return (

                                            <Tag
                                                color={
                                                    getCollectionColor(
                                                        record.collected,
                                                        record.fee
                                                    )
                                                }
                                            >
                                                {rate.toFixed(1)}%
                                            </Tag>

                                        );

                                    },
                            },

                        ]}
                    />

                </div>

            </Card>


            {/* =================================================
                MOBILE DOWNLOAD BAR
            ================================================= */}

            <div
                className="financial-download-bar"
                style={{
                    position:
                        "sticky",

                    bottom:
                        12,

                    zIndex:
                        20,

                    marginTop:
                        18,
                }}
            >

                <Card
                    bordered={false}
                    style={{
                        borderRadius:
                            16,

                        boxShadow:
                            "0 10px 35px rgba(15,23,42,.18)",
                    }}
                    bodyStyle={{
                        padding:
                            12,
                    }}
                >

                    <div
                        style={{
                            display:
                                "flex",

                            flexWrap:
                                "wrap",

                            gap:
                                8,
                        }}
                    >

                        <Button
                            icon={
                                <FilePdfOutlined />
                            }
                            onClick={
                                handleDownloadExecutive
                            }
                            loading={
                                downloading
                            }
                        >
                            Executive PDF
                        </Button>


                        <Button
                            icon={
                                <FilePdfOutlined />
                            }
                            onClick={
                                handleDownloadGrades
                            }
                            loading={
                                downloading
                            }
                        >
                            Grade PDF
                        </Button>


                        <Button
                            icon={
                                <FilePdfOutlined />
                            }
                            onClick={
                                handleDownloadStudentTypes
                            }
                            loading={
                                downloading
                            }
                        >
                            Student-Type PDF
                        </Button>


                        <Button
                            type="primary"
                            icon={
                                <CloudDownloadOutlined />
                            }
                            onClick={
                                handleDownloadAll
                            }
                            loading={
                                downloading
                            }
                        >
                            Download All Reports
                        </Button>

                    </div>

                </Card>

            </div>

        </div>

    );

};

export default SchoolFinancialReport;
